/**
 * Shared Theme Base
 *
 * `shared` is not a theme itself, more a core set of values used as a
 * foundational layer for each theme.
 *
 * As colors change per brand, we're only interested in typing the shared color
 * palettes here.
 */
// type OmitBrand = Omit<Theme, 'brand'>;
// type OmitColorScheme = Omit<OmitBrand, 'colorScheme'>;
// type Shared = Omit<OmitColorScheme, 'color'>;
const borderStyle = 'solid';
const borderWidth = 1;
const borderWidthDefault = 1;
const borderWidthInteractive = 2;
const space = 16;
const transitionDuration = 0.2;
const transitionTimingFunction = 'ease-in-out';
const baseLineHeight = 24;
const fontWeightNormal = 400;
const fontWeightSemibold = 500;
export const baseFontSize = 16;
export const rem = (value) => `${value / baseFontSize}rem`;
const sharedTokens = {
    base: {
        space,
        fontSize: 16,
        lineHeight: 20,
    },
    borderStyle,
    borderWidth,
    borderWidthInteractive,
    border: `${borderWidth}px ${borderStyle}`,
    boxShadow: '0px 0px 1px 0px #00000066, 0px 8px 24px -6px #00000029',
    shadows: {
        sm: '0px 0px 1px 0px #00000066, 0px 6px 6px -6px #00000029',
        md: '0px 0px 1px 0px #00000066, 0px 12px 12px -6px #00000029',
        lg: '0px 0px 1px 0px #00000066, 0px 8px 24px -6px #00000029',
        xl: '0px 0px 1px 0px #00000033, 0px 0px 32px -8px #0000001F, 0px 32px 32px -8px #00000014',
    },
    breakpoint: {
        small: 500,
        medium: 800,
        large: 1080,
        xlarge: 1200,
    },
    fontFace: undefined,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    fontSize: {
        body: 14,
    },
    fontWeight: {
        normal: 400,
        semibold: 500,
    },
    maxWidth: {
        default: 1024,
        large: 1440,
    },
    opacity: {
        disabled: 0.35,
    },
    radius: {
        small: space * 0.25,
        default: space * 0.5,
        largest: 100,
    },
    space: {
        xsmall: space * 0.25,
        small: space * 0.5,
        default: space,
        medium: space * 1.5,
        large: space * 2,
        xlarge: space * 2.5,
    },
    transitionDuration: {
        slow: transitionDuration * 2,
        default: transitionDuration,
    },
    transition: {
        slow: `${transitionDuration * 2}s ease-in-out`,
        default: `${transitionDuration}s ease-in-out`,
    },
    zIndex: {
        carouselControl: 5,
        selectOptions: 10,
        dialog: 10000,
        toggle: 1,
    },
    newTokens: {
        borderRadius: {
            none: 0,
            twoxsmall: rem(2),
            xsmall: rem(4),
            small: rem(6),
            medium: rem(8),
            large: rem(12),
            xlarge: rem(16),
            twoxlarge: rem(24),
            full: rem(9999),
            interactive: {
                xsmall: rem(4),
                small: rem(8),
                medium: rem(12),
            },
            surface: {
                xsmall: rem(4),
                small: rem(8),
                medium: rem(12),
                large: rem(16),
            },
        },
        breakpoint: {
            small: rem(640),
            medium: rem(768),
            large: rem(1024),
            xlarge: rem(1280),
            twoxlarge: rem(1536),
        },
        border: {
            default: `${rem(borderWidthDefault)} ${borderStyle}`,
            interactive: `${rem(borderWidthInteractive)} ${borderStyle}`,
            width: {
                default: rem(borderWidthDefault),
                interactive: rem(borderWidthInteractive),
            },
            style: borderStyle,
        },
        space: {
            threexsmall: rem(4),
            twoxsmall: rem(8),
            xsmall: rem(12),
            small: rem(16),
            medium: rem(24),
            large: rem(32),
            xlarge: rem(40),
            twoxlarge: rem(48),
            threexlarge: rem(56),
        },
        transition: {
            default: `${transitionDuration}s ${transitionTimingFunction}`,
            slow: `${transitionDuration * 2}s ${transitionTimingFunction}`,
        },
        zIndex: {
            carouselControl: 5,
            dialog: 10000,
            toggle: 1,
        },
        boxShadow: {
            small: `0 ${rem(6)} ${rem(6)} ${rem(-6)} rgba(0, 0, 0, 0.16), 0 0 ${rem(1)} rgba(0, 0, 0, 0.4)`,
            medium: `0 ${rem(12)} ${rem(12)} ${rem(-6)} rgba(0, 0, 0, 0.16), 0 0 ${rem(1)} rgba(0, 0, 0, 0.4)`,
            large: `0 ${rem(8)} ${rem(24)} ${rem(-6)} rgba(0, 0, 0, 0.16), 0 0 ${rem(1)} rgba(0, 0, 0, 0.4)`,
            xlarge: `0 ${rem(32)} ${rem(32)} ${rem(-8)} rgba(0, 0, 0, 0.08), 0 0 ${rem(32)} ${rem(-8)} rgba(0, 0, 0, 0.12), 0 0 ${rem(1)} rgba(0, 0, 0, 0.2)`,
        },
        font: {
            face: undefined,
            family: 'Averta Std, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
            size: rem(baseFontSize),
            weight: {
                normal: fontWeightNormal,
                semibold: fontWeightSemibold,
            },
            lineHeight: rem(baseLineHeight),
        },
        opacity: 0.32,
        transform: 'scale(0.9)',
    },
};
export default sharedTokens;
